import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRestaurantContext } from '../../context/RestaurantContext';
import useCart from '../../hooks/useCart';
import TopBarHeader from '../../components/ui/TopBarHeader';
import Icon from '../../components/AppIcon';
import JoinQueueSheet from '../menu-page/components/JoinQueueSheet';
import InfoSheet from '../menu-page/components/InfoSheet';
import PreOrderDetailsSheet from './components/PreOrderDetailsSheet';
import { useQueueContext } from '../../context/QueueContext';
import { getActiveQueueEntry, updateQueueStatus, checkQueueAvailability } from '../../services/api/restApiAdapter';

const QueueStatus = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const { theme, selfOrderConfig } = useRestaurantContext();
  const { cartData } = useCart();
  const { activeQueue, refreshQueue, isLoading: contextLoading } = useQueueContext();
  
  const [isJoinQueueSheetOpen, setIsJoinQueueSheetOpen] = useState(false);
  const [showQueueInfo, setShowQueueInfo] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [selectedPreOrderCart, setSelectedPreOrderCart] = useState(null);

  const themeColor = theme?.secondary || '#000';
  const themeTextColor = theme?.primary || '#fff';

  // No local fetch needed, handled by QueueContext

  const handleCancel = async (queueId) => {
    if (!window.confirm('Are you sure you want to cancel your spot in the queue?')) return;
    
    try {
      const response = await updateQueueStatus(restaurantId, queueId, 'cancelled');
      if (response.success) {
        refreshQueue();
      }
    } catch (err) {
      console.error('Error cancelling queue:', err);
    }
  };

  const handleJoinQueueClick = async () => {
    setIsCheckingAvailability(true);
    try {
      const response = await checkQueueAvailability(restaurantId);
      if (response.success && response.isAvailable) {
        setIsJoinQueueSheetOpen(true);
      } else {
        setShowQueueInfo(true);
      }
    } catch (err) {
      console.error('Error checking availability:', err);
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const renderContent = () => {
    if (contextLoading) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 space-y-4">
          <Icon name="Loader" className="animate-spin text-gray-300" size={32} />
          <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Updating status...</p>
        </div>
      );
    }

    const queueEntries = activeQueue ? [activeQueue] : [];

    if (queueEntries.length === 0) {
      return (
        <main className="flex-1 flex flex-col items-center justify-center p-8 text-center max-w-md mx-auto w-full">
          <div className="w-24 h-24 bg-white rounded-[32px] shadow-sm border border-gray-100 flex items-center justify-center mb-8">
            <Icon name="Users" size={40} className="text-gray-200" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-3 tracking-tight">No Active Queue Entry</h2>
          <p className="text-gray-500 text-sm font-medium leading-relaxed mb-10 px-4">
            You don't have an active spot in the waiting list for this restaurant. 
            Join the queue from the menu page to get started.
          </p>
          <div className="flex flex-col gap-3 w-full">
            <button
              onClick={handleJoinQueueClick}
              disabled={isCheckingAvailability}
              className="w-full py-4 bg-gray-900 text-white rounded-2xl font-black shadow-lg shadow-gray-200 transition-all active:scale-95 uppercase tracking-widest text-[10px] flex items-center justify-center gap-2"
              style={{ backgroundColor: themeColor, color: themeTextColor }}
            >
              {isCheckingAvailability ? (
                <Icon name="Loader" size={14} className="animate-spin" />
              ) : (
                'Join the Queue'
              )}
            </button>
            <button
              onClick={() => navigate(`/restaurant/${restaurantId}`)}
              className="w-full py-4 bg-white text-gray-400 border border-gray-200 rounded-2xl font-black transition-all active:scale-95 uppercase tracking-widest text-[10px]"
            >
              Back to Menu
            </button>
          </div>
        </main>
      );
    }

    return (
      <main className="flex-1 p-6 flex flex-col items-center max-w-md mx-auto w-full gap-6 overflow-y-auto">
        {queueEntries.map((queueEntry) => {
          const { 
            queueNumber, 
            queueCode,
            status, 
            guestDetails,
            createdAt, 
            selfOrderCart,
            positionInQueue
          } = queueEntry;

          const partySize = guestDetails?.partySize || 1;
          
          console.log('Queue Entry Debug:', { id: queueEntry.id, hasCart: !!selfOrderCart, cartType: typeof selfOrderCart });
          
          const isPreOrder = !!selfOrderCart && typeof selfOrderCart === 'object';
          const preOrderCode = selfOrderCart?.code;

          return (
            <motion.div
              key={queueEntry.id || queueEntry._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`w-full rounded-[32px] p-6 shadow-xl shadow-gray-100 border overflow-hidden relative transition-all duration-500 ${
                positionInQueue === 1 
                  ? 'bg-green-50/50 border-green-100 ring-4 ring-green-50/20' 
                  : 'bg-white border-gray-100'
              }`}
            >
              {/* Header: Status & Position */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${status === 'called' ? 'bg-green-500 animate-pulse' : 'bg-orange-500'}`} />
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    {status === 'called' ? 'Ready Now' : 'In Queue'}
                  </span>
                </div>
                {positionInQueue && (
                  <div className="flex items-center gap-2">
                    {positionInQueue === 1 && (
                      <div className="px-2 py-0.5 rounded-lg bg-green-500 text-white text-[8px] font-black uppercase tracking-widest shadow-sm shadow-green-200">
                        You're Next
                      </div>
                    )}
                    <div 
                      className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5"
                      style={{ backgroundColor: `${themeColor}15`, color: themeColor }}
                    >
                      <Icon name="Users" size={10} />
                      #{positionInQueue}
                    </div>
                  </div>
                )}
              </div>

              {/* Queue Number & Position Row */}
              <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Queue Number</span>
                  <div className="text-5xl font-black tracking-tighter text-gray-900 leading-none">
                    {queueCode || queueNumber}
                  </div>
                </div>

                <div className="flex-1 h-px bg-gray-50 mx-2" />

                <div className="flex flex-col items-end text-right">
                   <div className="flex items-center gap-2">
                    <span className="text-3xl font-black tracking-tighter" style={{ color: themeColor }}>
                      #{positionInQueue || '?'}
                    </span>
                   </div>
                   <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">In Line</span>
                </div>
              </div>

              {/* Status Message */}
              <div className={`mb-6 p-4 rounded-2xl border transition-all duration-500 ${
                positionInQueue === 1 
                  ? 'bg-green-100/50 border-green-200' 
                  : 'bg-gray-50 border-gray-100'
              }`}>
                <p className="text-sm text-gray-600 font-medium leading-relaxed">
                  {status === 'called' 
                    ? "Your table is ready! Please proceed to the host stand." 
                    : "We'll alert you as soon as your table is ready."}
                </p>
              </div>

              {/* Secondary Code (Pre-order) if applicable */}
              {isPreOrder && (
                <button 
                  onClick={() => setSelectedPreOrderCart(selfOrderCart)}
                  className="w-full mb-6 p-4 rounded-2xl border-2 border-dashed border-gray-100 flex items-center justify-between bg-white hover:bg-gray-50 active:scale-[0.99] transition-all text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500">
                      <Icon name="ShoppingBag" size={24} />
                    </div>
                    <div>
                      <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">Pre-order Items</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-black text-gray-900 tracking-tight">{preOrderCode}</span>
                        <span className="text-[10px] bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-black uppercase tracking-widest">
                          {selfOrderCart.items?.length || 0} Items
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-gray-300">
                    <Icon name="ChevronRight" size={20} />
                  </div>
                </button>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-gray-50/50 p-3 rounded-2xl border border-gray-50">
                  <span className="text-[9px] font-black uppercase tracking-widest text-gray-400 block mb-1">Party Size</span>
                  <span className="text-sm font-black text-gray-900">{partySize} Guests</span>
                </div>
                <div className="bg-gray-50/50 p-3 rounded-2xl border border-gray-50">
                  <span className="text-[9px] font-black uppercase tracking-widest text-gray-400 block mb-1">Joined At</span>
                  <span className="text-sm font-black text-gray-900">
                    {new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).format(new Date(createdAt))}
                  </span>
                </div>
              </div>

              {/* Cancel Button */}
              <div className="pt-4 border-t border-gray-50 flex justify-center">
                <button
                  onClick={() => handleCancel(queueEntry.id || queueEntry._id)}
                  className="px-6 py-2 rounded-full bg-red-50 text-red-600 font-black text-[9px] uppercase tracking-widest hover:bg-red-100 transition-colors"
                >
                  Cancel Spot
                </button>
              </div>
            </motion.div>
          );
        })}

        <button 
          onClick={() => navigate(`/restaurant/${restaurantId}`)}
          className="mt-6 mb-4 w-full py-4 rounded-2xl font-black text-gray-400 hover:text-gray-600 bg-white border border-gray-200 transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          <Icon name="ArrowLeft" size={18} /> Back to Menu
        </button>
      </main>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <TopBarHeader 
        title="Queue Status" 
        showBack 
        onBackClick={() => navigate(`/restaurant/${restaurantId}`)}
        hideCart
        hideMenu
        rightNode={
          <button
            onClick={() => navigate(`/restaurant/${restaurantId}/queue-history`)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-50 text-gray-900 hover:bg-gray-100 transition-all active:scale-95 shadow-sm"
          >
            <Icon name="History" size={20} />
          </button>
        }
      />
      
      {renderContent()}

      <JoinQueueSheet 
        isOpen={isJoinQueueSheetOpen}
        onClose={() => setIsJoinQueueSheetOpen(false)}
        restaurantId={restaurantId}
        theme={theme}
        onJoinSuccess={() => {
          setIsJoinQueueSheetOpen(false);
          refreshQueue();
        }}
      />

      <InfoSheet 
        isOpen={showQueueInfo}
        onClose={() => setShowQueueInfo(false)}
        title="Waitlist Unavailable"
        message="The waitlist is currently disabled or at capacity. Please check back later or speak with our staff."
        theme={theme}
      />

      <PreOrderDetailsSheet 
        isOpen={!!selectedPreOrderCart} 
        onClose={() => setSelectedPreOrderCart(null)}
        cart={selectedPreOrderCart}
        theme={theme}
      />
    </div>
  );
};

export default QueueStatus;
